import shallowCompare from "./shallowCompare";

/**
 * Memoizes a function but, unlike most memoizing functions, this one has a configurable capacity
 * which also evicts the least recently used entry when updating.
 *
 * @param f The function to memoize
 * @param capacity The number of cache lines
 * @returns A new, memoized version of the function
 */
export default function lruMemo<T extends object, V>(
  f: (t: T) => V,
  capacity: number = 4,
  compare: (a: T, b: T) => boolean = shallowCompare,
): (t: T) => V {
  const cache: [T, V][] = [];

  return (newArgs: T) => {
    for (const [arg, res] of cache) {
      if (compare(arg, newArgs)) {
        return res;
      }
    }

    const newRes = f(newArgs);
    cache.push([newArgs, newRes]);
    if (cache.length > capacity) {
      cache.shift();
    }

    return newRes;
  };
}
